import React, {useContext, useEffect, useState} from "react";
import {AbuseCheckStatus, AbuseWord, PatternTypes} from "services/abuseWordsService";
import {AppEmojis, AppNames, Apps, GenderEmoji, GenderName, Genders, RootRoutes} from "enums";
import {ColDef, ColGroupDef} from 'ag-grid-community';
import {CustomCellRendererProps} from "ag-grid-react";
import {Button} from "react-bootstrap";
import {AbuseWordsTagFilter} from "pages/AbuseWords/filters/AbuseWordsTagFilter";
import {AbuseGenderFilter} from "pages/AbuseWords/filters/AbuseGenderFilter";
import {AbuseAppIdsFilter} from "pages/AbuseWords/filters/AbuseAppIdsFilter";
import {AbuseActionTypesFilter} from "pages/AbuseWords/filters/AbuseActionTypesFilter";
import {AbuseActionTypeTag} from "pages/AbuseWords/components/AbuseActionTypeTag";
import {AbusePatternTypeTag} from "pages/AbuseWords/components/AbusePatternTypeTag";
import {AbusePatternTypesFilter} from "pages/AbuseWords/filters/AbusePatternTypesFilter";
import {AbuseWordsLanguageFilter} from "pages/AbuseWords/filters/AbuseWordsLanguageFilter";
import {AuthContext} from "contexts";
import {toDateString} from "utils";
import {Link, NavigateFunction, useNavigate} from "react-router-dom";
import {AbuseCheckStatusLabels} from "pages/AbuseWords/components/AbuseCheckStatusLabels";

const PATTERN_TYPES_COUNT = Object.values(PatternTypes).filter(v => typeof (v) === 'number').length

const getColumnDefs = (app: Apps, onEdit: (id: number) => void, onDelete: (id: number) => void, onStartCheck: (id: number) => void, navigate: NavigateFunction, appIdsSupported: boolean): (ColDef<AbuseWord> | ColGroupDef<AbuseWord>)[] => {
    const ActionButtonsCell = (params: CustomCellRendererProps) => {
        return <div className="d-flex flex-row align-items-center h-100" style={{gap: 8}}>
            <Button size="sm" variant='secondary' onClick={() => onEdit(params.value)}>Редактировать</Button>
            <Button size="sm" variant='outline-danger' onClick={() => onDelete(params.value)}>Удалить</Button>
            <Button size="sm" variant='outline-primary' onClick={() => onStartCheck(params.value)}>Проверка</Button>
        </div>
    };

    return [
        {
            headerName: 'Тег',
            cellRenderer: (params: CustomCellRendererProps) => {
                return (
                    <span className="badge bg-dark">{params.data?.tag}</span>
                );
            },
            filter: AbuseWordsTagFilter,
            flex: 1,
        },
        {
            headerName: 'Язык',
            cellRenderer: (params: CustomCellRendererProps) => {
                return (
                    <span className="badge bg-secondary">{params.data?.language}</span>
                );
            },
            filter: AbuseWordsLanguageFilter,
            flex: 0.7,
        },
        {
            headerName: 'Название',
            field: 'ruleName',
            flex: 1.5,
            filter: true,
            cellRenderer: (params: CustomCellRendererProps) => {
                return <Link target='_blank'
                             to={`/${RootRoutes.AbuseWordsProfiles}?id=${params.data?.id}`}
                             className="link-dark link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover">
                    {params.data?.ruleName}
                </Link>
            },
        },
        ...(appIdsSupported ? [{
            headerName: 'Приложения',
            valueGetter: (v: any) => {
                return v.data?.appIds?.map((id: number) => `${AppEmojis[id] ?? ''} ${AppNames[id] ?? id}`.trim()).join(', ')
            },
            tooltipValueGetter: (v: any) => v.data?.appIds?.map((id: number) => AppNames[id] ?? id).join(', '),
            filter: AbuseAppIdsFilter,
            flex: 1,
        } as ColDef<AbuseWord>] : []),
        {
            headerName: 'Гендеры',
            valueGetter: (v) => {
                return v.data?.genders.sort((a, b) => +a - +b).map(g => GenderEmoji[g.toString() as Genders]).filter(g => g)
            },
            filter: AbuseGenderFilter,
            flex: 0.7,
            tooltipValueGetter: (v) => v.data?.genders.map(g => GenderName[g]).join(', ')
        },
        {
            headerName: 'Паттерн',
            field: 'pattern',
            flex: 1,
            filter: true,
            tooltipValueGetter: (v) => v.data?.pattern
        },
        {
            headerName: 'Тип',
            field: 'patternTypes',
            cellRenderer: (params: CustomCellRendererProps) => {
                return params.value?.length === PATTERN_TYPES_COUNT ? '' : params.value?.map((v: PatternTypes) =>
                    <AbusePatternTypeTag patternType={v}/>)
            },
            flex: 1,
            filter: AbusePatternTypesFilter,
        },
        {
            headerName: 'Действие',
            field: 'actionType',
            cellRenderer: (params: CustomCellRendererProps) => {
                return <AbuseActionTypeTag app={app ?? Apps.Instinct} action={params.value}/>
            },
            flex: 1.5,
            filter: AbuseActionTypesFilter,
        },
        {
            headerName: 'Пользователей',
            field: 'stats.userCount',
            flex: 0.5,
        },
        {
            headerName: '% бана',
            valueGetter: (v) => {
                return v.data?.stats ? Math.round(((v.data?.stats?.bannedByReviewCount ?? 0) / (v.data?.stats?.userCount ?? 1)) * 100) : ''
            },
            flex: 0.7,
        },
        {
            headerName: 'Автор',
            valueGetter: (v) => {
                return v.data?.lastChanged?.userName
            },
            tooltipValueGetter: (v) => v.data?.lastChanged?.userName,
            flex: 1
        },
        {
            headerName: 'Проверка',
            valueGetter: (v) => {
                const word = (v.data as any as AbuseWord);
                const status = word.activeCheck?.status ? word.activeCheck?.status as AbuseCheckStatus : null;
                if (word.pattern === 'ruderomp') {
                    console.log({a: word.activeCheck, status})
                }
                return status ? '🔄' : ''
            },
            tooltipValueGetter: (v) => {
                const word = (v.data as any as AbuseWord);
                const status = word.activeCheck?.status ? word.activeCheck?.status as AbuseCheckStatus : null;
                return status ? `${AbuseCheckStatusLabels[status]} (${word?.activeCheck?.progressPercent}%)` : ''
            },
            flex: 0.7
        },
        {
            headerName: 'Дата',
            valueGetter: (v) => v.data?.lastChanged?.lastChangedDate ?? null,
            valueFormatter: (v) => v.value ? toDateString(v.value, false) ?? '' : '',
            flex: 1
        },
        {
            field: 'id',
            headerName: "Действия",
            cellRenderer: ActionButtonsCell,
            flex: 2.5
        },
    ]
}


export const useAbuseWordsTable = (onEdit: (id: number) => void, onDelete: (id: number) => void, onStartCheck: (id: number) => void, appIdsSupported: boolean) => {
    const {env} = useContext(AuthContext);
    const navigate = useNavigate();

    const [columnDefs, setColumnDefs] = useState<(ColDef<AbuseWord> | ColGroupDef<AbuseWord>)[]>(getColumnDefs(env?.app ?? Apps.Instinct, onEdit, onDelete, onStartCheck, navigate, appIdsSupported));

    useEffect(() => {
        setColumnDefs(getColumnDefs(env?.app ?? Apps.Instinct, onEdit, onDelete, onStartCheck, navigate, appIdsSupported))
    }, [env?.app, appIdsSupported]);


    return {columnDefs}
}